import React, { useState } from "react";
import { Send, Phone, Mail, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";

const interestOptions = [
  "Request a Free Test Print",
  "Talk to a Formlabs Expert",
  "Get a Quote on a Package",
  "Book a Showroom Demo",
  "Resin Recommendation",
  "Service & Repairs",
  "Other",
];

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    interest: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.interest) {
      toast.error("Please fill in your name, email, and area of interest.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await res.json();
      if (res.ok) {
        setSubmitted(true);
        toast.success("Thanks! Our team will be in touch shortly.");
      } else {
        toast.error(result.detail || "Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Network error. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Toaster position="top-center" />
        <section className="py-24 lg:py-32 bg-[#0A0A0A] relative">
          <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#F76B1C]/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-[#F76B1C]" />
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
              Thanks for Reaching Out!
            </h3>
            <p className="text-white/45 leading-relaxed mb-8">
              Our team will review your enquiry and get back to you within 1
              business day. In the meantime, feel free to call us directly.
            </p>
            <a
              href="tel:1300334327"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#F76B1C] hover:bg-[#E05A0F] text-white font-semibold rounded-xl transition-colors text-sm"
            >
              <Phone size={16} />
              1300 334 327
            </a>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      <section id="contact" className="py-24 lg:py-32 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#F76B1C]/[0.015] rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16">
            {/* Left: Info */}
            <div className="flex flex-col justify-center">
              <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em] mb-4">
                Get in Touch
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Request a Test Print or Talk to an Expert
              </h2>
              <p className="text-lg text-white/45 leading-relaxed mb-10">
                Not sure which printer or resin is right for you? Our team has
                15+ years of experience and can help you find the perfect setup.
                We can even print a free sample in your chosen material.
              </p>

              <div className="space-y-4">
                <a
                  href="tel:1300334327"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#F76B1C]/10 flex items-center justify-center group-hover:bg-[#F76B1C]/20 transition-colors">
                    <Phone size={18} className="text-[#F76B1C]" />
                  </div>
                  <div>
                    <p className="text-xs text-white/30">Call Us</p>
                    <p className="text-white font-semibold group-hover:text-[#F76B1C] transition-colors">
                      1300 334 327
                    </p>
                  </div>
                </a>
                <a
                  href="mailto:admin@3dprintergear.com.au"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#F76B1C]/10 flex items-center justify-center group-hover:bg-[#F76B1C]/20 transition-colors">
                    <Mail size={18} className="text-[#F76B1C]" />
                  </div>
                  <div>
                    <p className="text-xs text-white/30">Email Us</p>
                    <p className="text-white font-semibold group-hover:text-[#F76B1C] transition-colors">
                      admin@3dprintergear.com.au
                    </p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className="rounded-2xl bg-[#111111] border border-white/[0.06] p-7 lg:p-10">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-white/40 font-medium mb-1.5">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#F76B1C]/40 focus:ring-1 focus:ring-[#F76B1C]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 font-medium mb-1.5">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#F76B1C]/40 focus:ring-1 focus:ring-[#F76B1C]/20 transition-all"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-white/40 font-medium mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="04XX XXX XXX"
                      className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#F76B1C]/40 focus:ring-1 focus:ring-[#F76B1C]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 font-medium mb-1.5">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Optional"
                      className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#F76B1C]/40 focus:ring-1 focus:ring-[#F76B1C]/20 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-white/40 font-medium mb-1.5">
                    I'm interested in... *
                  </label>
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white focus:outline-none focus:border-[#F76B1C]/40 focus:ring-1 focus:ring-[#F76B1C]/20 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-[#111111] text-white/50">
                      Select an option
                    </option>
                    {interestOptions.map((opt) => (
                      <option
                        key={opt}
                        value={opt}
                        className="bg-[#111111] text-white"
                      >
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-white/40 font-medium mb-1.5">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us about your project, the parts you need to print, or any questions you have..."
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#F76B1C]/40 focus:ring-1 focus:ring-[#F76B1C]/20 transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#F76B1C] hover:bg-[#E05A0F] text-white font-semibold rounded-xl transition-colors duration-200 hover:shadow-lg hover:shadow-[#F76B1C]/20 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={16} className={isSubmitting ? "animate-pulse" : ""} />
                  {isSubmitting ? "Sending..." : "Send Enquiry"}
                </button>

                <p className="text-[11px] text-white/20 text-center">
                  We'll respond within 1 business day. Your information is kept
                  private and never shared.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
